import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { calculateAmount } from './api/booking'
import { useBooking, cartIsCalculable } from '../store/bookingStore'
import type { CalculateAmount } from './api/types'

/**
 * Recomputes the live price whenever the cart changes and mirrors it into the
 * store so the PriceSummary and steps stay in sync. The amount is authoritative
 * from the backend — we never adjust it client-side, or confirmReservation
 * rejects the booking ("CalculatedInfo check failed").
 */
export function useCalculatedPrice() {
  const cart = useBooking((s) => s.cart)
  const setCalculated = useBooking((s) => s.setCalculated)
  const enabled = cartIsCalculable(cart)
  // The breakdown carries backend text (vehicle, services, discounts), so it is per-language.
  const { i18n } = useTranslation()
  const locale = i18n.language

  const query = useQuery<CalculateAmount>({
    queryKey: ['calculateAmount', cart, locale],
    queryFn: () => calculateAmount(cart),
    enabled,
    staleTime: 30_000,
    retry: 0,
  })

  useEffect(() => {
    if (query.data) setCalculated(query.data)
  }, [query.data, setCalculated])

  return query
}
